import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { IFreeWheelPauseAdModel } from 'tag/ts/tag/interfaces/ads/pause/IFreeWheelPauseAdModel';
import { IFWPauseAdGlobalParams } from 'tag/ts/tag/interfaces/ads/pause/IFWPauseAdGlobalParams';
import { IFWPauseAdSlotParams } from 'tag/ts/tag/interfaces/ads/pause/IFWPauseAdSlotParams';
import { IFreeWheelPauseAdModelArgument } from 'tag/ts/tag/interfaces/arguments/IFreeWheelPauseAdModelArgument';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { FWPauseAdCustomParams } from 'tag/ts/tag/model/ads/pause/FWPauseAdCustomParams';
import { FWPauseAdGlobalParams } from 'tag/ts/tag/model/ads/pause/FWPauseAdGlobalParams';
import { FWPauseAdSiteParams } from 'tag/ts/tag/model/ads/pause/FWPauseAdSiteParams';
import { FWPauseAdSlotParams } from 'tag/ts/tag/model/ads/pause/FWPauseAdSlotParams';
import { FWPauseAdSTVParams } from 'tag/ts/tag/model/ads/pause/FWPauseAdSTVParams';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { TFWPauseAdMergedParams } from 'tag/ts/tag/types/ads/pause/TFWPauseAdMergedParams';
import { TFWPauseAdParams } from 'tag/ts/tag/types/ads/pause/TFWPauseAdParams';

/**
 * @copyright © Paramount Streaming
 * @description Contains all data needed to make Freewheel pause ad calls
 * @implements IFreeWheelPauseAdModel
 */
export class FreeWheelPauseAdModel implements IFreeWheelPauseAdModel {
	/**
	 * @description FreeWheel Pause Ad Model name
	 */
	readonly #name: string;
	/**
	 * @description FreeWheel global parameters
	 */
	readonly #globalParams: FWPauseAdGlobalParams;
	/**
	 * @description Freewheel merged parameters
	 */
	readonly #mergedParams: TFWPauseAdMergedParams;
	/**
	 * @description Freewheel slot parameters
	 */
	readonly #slotParams: FWPauseAdSlotParams;
	/**
	 * @description All Freewheel parameters
	 */
	readonly #params: TFWPauseAdParams;

	readonly #tag: ITag;

	constructor( argument: IFreeWheelPauseAdModelArgument ) {
		argument.name = 'FreeWheelPauseAdModel';
		this.#tag = argument?.tag;
		this.#name = argument?.name;

		// get ad setup info
		const adSetupInfo: IAdSetupInfo = this.#tag?.model?.adUtilities?.adSetupInfo();

		switch ( argument?.embedType ) {
			case EmbedType.DESKTOP:
				const siteParams: FWPauseAdSiteParams = new FWPauseAdSiteParams( { tag: this.#tag } );
				const customParams: FWPauseAdCustomParams = new FWPauseAdCustomParams( { tag: this.#tag } );

				this.#globalParams = new FWPauseAdGlobalParams( { tag: this.#tag } );
				this.#mergedParams = {
					...( siteParams.adParameters || {} ),
					...( customParams.adParameters || {} )
				};
				this.#slotParams = new FWPauseAdSlotParams( EmbedType.DESKTOP );
				break;

			case EmbedType.HTML5:
				const stvParams: FWPauseAdSTVParams = new FWPauseAdSTVParams( this.#tag );
				this.#globalParams = new FWPauseAdGlobalParams( { tag: this.#tag, paramsOverride: stvParams.paramsOverride } );
				this.#mergedParams = {
					...( stvParams.params || {} )
				};
				this.#slotParams = new FWPauseAdSlotParams( EmbedType.HTML5 );
				break;
		}

		this.#params = {
			...( this.#globalParams.adParameters || {} ),
			...( this.#mergedParams || {} ),
			...( this.#slotParams.params || {} )
		};
	}

	/**
	 * @description Returns FreeWheel Pause Ad Model name
	 * @returns {string}
	 */
	get name(): string {
		return this.#name;
	}

	/**
	 * @description Returns global parameters
	 * @returns {IFWPauseAdGlobalParams}
	 */
	get globalParams(): IFWPauseAdGlobalParams {
		return this.#globalParams.adParameters;
	}

	/**
	 * @description Returns merged parameters
	 * @returns {TFWPauseAdMergedParams}
	 */
	get mergedParams(): TFWPauseAdMergedParams {
		return this.#mergedParams;
	}

	/**
	 * @description Returns slot parameters
	 * @returns {IFWPauseAdSlotParams}
	 */
	get slotParams(): IFWPauseAdSlotParams {
		return this.#slotParams.params;
	}

	/**
	 * @description Returns all FreeWheel parameters
	 * @returns {TFWPauseAdParams}
	 */
	get params(): TFWPauseAdParams {
		return this.#params;
	}
}